import { useCallback, useRef } from "react";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { executeRolePermissionsRefresh, type Matrix } from "./useRolePermissionsRefreshExecutor";
import { useRolePermissionsEventListeners } from "./useRolePermissionsRefreshListeners";

export type { Matrix } from "./useRolePermissionsRefreshExecutor";

export type UseRolePermissionsRefreshLogicParams = {
	isRefreshingRef: React.MutableRefObject<boolean>;
	lastRefreshRef: React.MutableRefObject<number>;
	setMatrix: (value: Matrix) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
	setLoading: (value: boolean) => void;
	setError: (value: string | null) => void;
};

function useRolePermissionsRefreshLogic(params: UseRolePermissionsRefreshLogicParams): () => Promise<void> {
	const { isRefreshingRef, lastRefreshRef, setMatrix, setPermissionContext, setLoading, setError } = params;
	return useCallback(async () => {
		await executeRolePermissionsRefresh({
			isRefreshingRef,
			lastRefreshRef,
			setMatrix,
			setPermissionContext,
			setLoading,
			setError,
		});
	}, [isRefreshingRef, lastRefreshRef, setMatrix, setPermissionContext, setLoading, setError]);
}

/**
 * Hook for managing role permissions refresh logic and event listeners
 */
export function useRolePermissionsRefresh(
	setMatrix: (value: Matrix) => void,
	setPermissionContext: (value: PermissionContextDto | null) => void,
	setLoading: (value: boolean) => void,
	setError: (value: string | null) => void,
): () => Promise<void> {
	// Track if a refresh is in progress to avoid multiple simultaneous calls
	const isRefreshingRef = useRef(false);
	// Track the last refresh timestamp to implement debouncing
	const lastRefreshRef = useRef<number>(0);

	const refresh = useRolePermissionsRefreshLogic({
		isRefreshingRef,
		lastRefreshRef,
		setMatrix,
		setPermissionContext,
		setLoading,
		setError,
	});

	useRolePermissionsEventListeners(refresh, setMatrix, setPermissionContext, setLoading);

	return refresh;
}
